import { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { TopBar } from "../../../components/TopBar";
import Box from "@mui/material/Box";
import { Typography, Button, Portal } from "@mui/material";
import { NoResults } from "../../../../../../schema/src/app/components/NoResults";
import SearchBox from "../../../../../../../shell/components/SearchBox";
import FontFamilyCard from "./FontFamilyCard";
import { useInstalledFonts } from "../Installed";
import { useSelector } from "react-redux";
import { AppState } from "../../../../../../../shell/store/types";
type InstalledFont = {
  ZUID: string;
  family: string;
  variants: string[];
  href: string;
};
export type FontAndVariants = {
  family: string;
  variants: string[];
};
export const getFontDataFromHref = (url: string): FontAndVariants => {
  if (!url) return {
    family: "",
    variants: []
  };
  const fontUrlData = new URL(url);
  const fontFamilyAndVariants = fontUrlData?.searchParams?.get("family");
  const [family, variantsString = ""] = fontFamilyAndVariants?.split(":") || [];
  const variants = variantsString?.split(",") || [];
  return {
    family,
    variants
  };
};
const Browse = () => {
  const {
    t
  } = useTranslation();
  const [loadComplete, setLoadComplete] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [prevActivePage, setPrevActivePage] = useState<number>(0);
  const [activePage, setActivePage] = useState<number>(0);
  const [activePagination, setActivePagination] = useState([]);
  const [previewText, setPreviewText] = useState<string>("");
  const webFonts = useSelector(({
    settings
  }: AppState) => settings?.fonts);
  const {
    fonts: installedFonts
  } = useInstalledFonts();
  const fontData = useMemo(() => {
    if (!webFonts?.length) {
      return [];
    } else {
      const paginationWithZUID = [...webFonts]?.sort((a, b) => a?.family?.localeCompare(b?.family))?.map((gFont, index) => {
        const currentInstalledFonts = installedFonts?.find((item: InstalledFont) => {
          const {
            family
          } = getFontDataFromHref(item?.href);
          return family === gFont?.family;
        });
        const {
          variants
        } = getFontDataFromHref(currentInstalledFonts?.href);
        return {
          index: index,
          family: gFont?.family,
          ZUID: currentInstalledFonts?.ZUID || null,
          variants: gFont?.variants,
          installedVariants: variants
        };
      });
      setLoadComplete(true);
      return paginationWithZUID;
    }
  }, [installedFonts, webFonts]);
  const paginationData = useMemo(() => {
    if (!fontData) return [];
    if (!search) return fontData;
    const searchFilteredFontData = fontData?.filter(font => font?.family?.toLowerCase().includes(search.toLowerCase()));
    return searchFilteredFontData;
  }, [search, fontData]);
  useEffect(() => {
    if (!loadComplete || !paginationData) return;
    const start = activePage * 10;
    const end = start + 10;
    const currentPageData = paginationData.slice(start, end);
    setActivePagination([...currentPageData]);
  }, [paginationData, loadComplete, activePage]);
  return <>
      {!!activePagination?.length && <Portal container={document.head}>
          {activePagination?.map(itemFont => <link key={itemFont?.family} rel="stylesheet" href={`https://fonts.googleapis.com/css?family=${itemFont?.family?.replace(/\s/g, "+")}`} />)}
        </Portal>}
      <TopBar title={t("settings.browseFontsTitle")} isNotSaved={false} isLoading={false} saveHidden data-sentry-element="TopBar" data-sentry-source-file="index.tsx">
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" columnGap={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <SearchBox data-cy="BrowseFontSearchInput" placeholder={t("settings.searchFontsPlaceholder")} type="text" variant="outlined" size="small" value={search} onChange={evt => {
          if (activePage !== 0 && !!evt.target.value) {
            setActivePage(0);
            setPrevActivePage(activePage);
          }
          if (activePage === 0 && !evt.target.value && activePage !== prevActivePage) {
            setActivePage(prevActivePage);
            setPrevActivePage(prevActivePage);
          }
          setSearch(evt.target.value);
        }} inputRef={searchInputRef} InputProps={{
          startAdornment: <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>,
          sx: {
            bgcolor: "grey.50"
          }
        }} sx={{
          width: "280px"
        }} data-sentry-element="SearchBox" data-sentry-source-file="index.tsx" />
        </Box>
      </TopBar>

      <Box className="main-wrapper" px={4} sx={{
      width: "100%",
      height: "calc(100% - 84px)",
      overflowY: "auto",
      overflowX: "hidden",
      margin: 0,
      display: "block",
      maxHeight: "calc(100% - 84px)",
      position: "relative",
      boxSizing: "border-box"
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Box py={2} height="100%" display="flex" flexDirection="column" justifyContent="flex-start" sx={{
        minHeight: "100%",
        boxSizing: "border-box"
      }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Box sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "1px solid",
          borderColor: "border",
          pb: 3
        }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <TextField id="previewText" placeholder={t("settings.typeSomethingToPreview")} type="text" variant="outlined" size="small" name="previewText" value={previewText} onChange={evt => setPreviewText(evt.target.value)} sx={{
            width: "60%",
            minWidth: "300px"
          }} data-sentry-element="TextField" data-sentry-source-file="index.tsx" />
          </Box>
          {!!search && paginationData?.length < 1 ? <Box width="100%" height="100%" display="grid" alignContent="center" data-cy="BrowseFontNoResultsContainer">
              <NoResults type="search" searchTerm={search} onButtonClick={() => {
            setSearch("");
            setActivePage(prevActivePage);
            searchInputRef?.current?.focus();
          }} />
            </Box> : <>
              <Box width="100%" data-cy="FontListContainer">
                {activePagination?.map((itemFont, index) => <FontFamilyCard previewText={previewText} key={itemFont?.family + index} family={itemFont?.family} variants={[...itemFont?.variants]} installedVariants={itemFont?.installedVariants} ZUID={itemFont?.ZUID} activePage={activePage + 1} />)}
              </Box>
              <Box width="100%" display="flex" flexDirection="row" justifyContent="center" alignItems="center" py={5} boxSizing="border-box">
                <Button variant="contained" color="primary" size="large" onClick={() => {
              setActivePage(prev => prev - 1);
            }} disabled={activePage === 0} sx={{
              py: 2,
              width: 64
            }}>
                  {t("settings.prev")}
                </Button>
                <Typography variant="h5" sx={{
              width: "85px",
              textAlign: "center",
              color: "text.secondary"
            }} noWrap>
                  {`${activePage + 1} / ${Math.ceil(paginationData?.length / 10) || 1}`}
                </Typography>
                <Button variant="contained" color="primary" onClick={() => {
              setActivePage(prev => prev + 1);
            }} disabled={activePage >= Math.ceil(paginationData?.length / 10) - 1} sx={{
              py: 2,
              width: 64
            }}>
                  {t("common.next")}
                </Button>
              </Box>
            </>}
        </Box>
      </Box>
    </>;
};
export default Browse;